import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, Truck, Star, Check, Loader2 } from 'lucide-react';

interface PriceCategory {
  id: number;
  name: string;
  description?: string;
  type?: string;
  basePrice?: number;
  pricePerKg?: number;
  pricePerKm?: number;
  minPrice?: number;
  maxPrice?: number;
  minWeight?: number;
  maxWeight?: number;
  applicableZones?: string;
  applicableServices?: string;
  isActive: boolean;
  branchId?: number;
  priority?: number;
  createdAt?: string;
}

export default function PricingPage() {
  const [categories, setCategories] = useState<PriceCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/pricecategories');
      if (res.data?.success) {
        const activeCategories = Array.isArray(res.data.data) 
          ? res.data.data.filter((c: any) => c.isActive) 
          : [];
        setCategories(activeCategories);
      }
    } catch (e) { 
      console.error(e); 
    }
    finally { 
      setLoading(false); 
    }
  };

  const getPlanIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'express': return Truck;
      case 'premium': return Star;
      default: return Package;
    }
  };

  const getPlanColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'express': return 'from-red-500 to-orange-600';
      case 'premium': return 'from-yellow-500 to-amber-600';
      default: return 'from-blue-500 to-indigo-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4" dir="rtl">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            باقات الأسعار
          </h1>
          <p className="text-xl text-gray-600">
            اختر الباقة المناسبة لاحتياجاتك في الشحن
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {categories.map((category) => {
            const Icon = getPlanIcon(category.type || '');
            const color = getPlanColor(category.type || '');
            
            return (
              <Card key={category.id} className="border-2 hover:border-orange-500 transition-all duration-300 hover:shadow-2xl">
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${color} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {category.name}
                  </h3>
                  
                  {category.description && (
                    <p className="text-gray-600 mb-6">
                      {category.description}
                    </p>
                  )}

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-bold text-gray-900">
                        {category.basePrice?.toFixed(0) || 0}
                      </span>
                      <span className="text-gray-600">ج.م</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      سعر البداية
                    </p>
                  </div>

                  <div className="space-y-3 mb-8">
                    {category.pricePerKg && (
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">
                          {category.pricePerKg.toFixed(2)} ج.م لكل كجم
                        </span>
                      </div>
                    )}
                    {category.pricePerKm && (
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">
                          {category.pricePerKm.toFixed(2)} ج.م لكل كم
                        </span>
                      </div>
                    )}
                    {category.minWeight && (
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">
                          الحد الأدنى: {category.minWeight} كجم
                        </span>
                      </div>
                    )}
                    {category.maxWeight && (
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">
                          الحد الأقصى: {category.maxWeight} كجم
                        </span>
                      </div>
                    )}
                  </div>

                  <Button 
                    className={`w-full bg-gradient-to-l ${color} hover:opacity-90 text-white py-6 text-lg font-semibold`}
                  >
                    اختر الباقة
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-blue-900 mb-4">
              هل تحتاج إلى باقة مخصصة؟
            </h3>
            <p className="text-blue-700 mb-6">
              تواصل معنا للحصول على عرض سعر مخصص لاحتياجاتك
            </p>
            <Button variant="outline" className="border-blue-600 text-blue-700 hover:bg-blue-100">
              اتصل بنا
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
